import { useEffect, useMemo } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { Link } from 'react-router-dom'

import { generateChatPath } from '@bettermode/common/utils'
import {
  useAuthMember,
  useNetwork,
  useFindChatWithMember,
} from '@tribeplatform/react-sdk/hooks'
import {
  GroupChatCreationPermission,
  RoleType,
} from '@tribeplatform/react-sdk/types'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { T } from '../../../i18n/index.js'
import { networkFields } from '../../constants.js'
import { NewChatFormFields } from '../../types.js'

import { ExistingChatPreview } from './ExistingChatPreview.js'

export const NoMemberSelected = () => {
  const { control } = useFormContext()
  const { data: authMember } = useAuthMember()
  const { data: network } = useNetwork({ fields: networkFields })
  const { whoCanCreateGroups } = network?.settings?.privateMessaging || {}
  const isStaff =
    authMember?.role?.type === RoleType.moderator ||
    authMember?.role?.type === RoleType.admin
  const anyoneCanCreateGroups =
    whoCanCreateGroups === GroupChatCreationPermission.Anyone
  const canCreateGroup = isStaff || anyoneCanCreateGroups
  const members: NewChatFormFields['members'] = useWatch({
    control,
    name: 'members',
  })
  const selectedMember = useMemo(() => {
    if (Array.isArray(members) && members.length === 0) {
      return null
    }

    if (Array.isArray(members)) {
      return members.at(0)
    }

    return members
  }, [members])
  const navigate = useNavigate()

  const { data: chat } = useFindChatWithMember({
    variables: {
      recipientId: selectedMember?.id,
    },
    useQueryOptions: {
      enabled: !!selectedMember,
      refetchOnWindowFocus: false,
      retry: false,
    },
  })

  useEffect(() => {
    if (chat && !canCreateGroup) {
      navigate(generateChatPath(chat._id), { replace: true })
    }
  }, [chat, canCreateGroup, navigate])

  const showContinueWithExistingChat = !!chat

  return (
    <div className="grid grid-rows-[1fr_auto_1fr] gap-y-6 overflow-hidden h-full w-full max-w-prose mx-auto">
      {showContinueWithExistingChat ? (
        <div className="flex flex-col animate-fade-in min-w-full overflow-y-auto">
          <ExistingChatPreview chatId={chat._id} />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-end animate-fade-in">
          <SvgIcon name="members" size="3x" className="text-content-muted" />
          <Text className="flex text-2xl font-semibold text-content-muted">
            <T
              id="Messaging.NewMessage.NoMemberSelected.Title"
              defaultMessage="Search for someone and start chatting."
            />
          </Text>
        </div>
      )}
      {canCreateGroup && (
        <>
          <div className="flex justify-center items-start">
            <div className="w-full max-w-[70%]">
              <Divider padding="none">
                <Text variant="bodySm" className="text-content-subdued">
                  <T id="Generics.or" defaultMessage="or" />
                </Text>
              </Divider>
            </div>
          </div>
          <div className="flex justify-center items-start animate-fade-in">
            <Button
              as={Link}
              to="/messaging/new?mode=group"
              variant="secondaryNeutral"
              trailingIcon={<SvgIcon className="ms-2" name="plus" />}
            >
              <T
                defaultMessage="Create a group chat"
                id="Messaging.NewMessage.NewGroupChat"
              />
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
